import React from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory} from 'react-router-dom'

import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import {Box, Chip, Typography, Theme} from '@mui/material'
import {PGButton} from '@progynyglobal/pg-ui-library'
import {useProductLine} from 'hooks/useProductLine'
import {makeStyles} from 'tss-react/mui'
import {getCancellationUrl} from 'utils/calcom'

export interface AppointmentCardProps {
  status: 'upcoming' | 'notAttended' | 'completed'
  session?: string
  title?: string
  subtitle?: string
  date: string
  time: string
  description?: string
  colors?: any // Product line colors
  programSlug?: string
  sessionNumber?: number
  canJoin?: boolean | null
  meetingLink?: string | null
  appointment?: {
    id?: string | null
    calcomBookingUid?: string | null
    canReschedule?: boolean | null
  } | null
  /** When true, the status chip (Upcoming/Past/Completed) is not shown. */
  hideStatus?: boolean
  /** Optional label for the type of consultation (e.g. "Emotional program", "General"). */
  consultationTypeLabel?: string
  /** When set with consultationTypeLabel, styles the chip: light primary for emotional_program, light green for general. */
  consultationType?: 'emotional_program' | 'general'
  /** Consultation hub slug used for onboarding reschedule route (e.g. pregnancy, postpartum, menopause). */
  consultationSlug?: string
  /** Callback invoked when a cancel action is requested. */
  onCancelRequested?: (cancelUrl: string) => void
}

const useStyles = makeStyles<{colors?: any}>()((theme: Theme, {colors}) => ({
  card: {
    display: 'flex',
    flexDirection: 'row',
    gap: '24px',
    padding: '32px',
    background: '#FFFFFF',
    boxShadow: '0px 3px 16px rgba(190, 192, 198, 0.24)',
    borderRadius: '8px',
    alignItems: 'flex-start',
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'row',
    gap: '24px',
    flex: 1,
    alignItems: 'flex-start',
  },
  iconColumn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  iconWrap: {
    width: '48px',
    height: '48px',
    background: '#F3F4F6',
    borderRadius: '1000px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentColumn: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  statusLabel: {
    padding: '6px 16px 4px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: 500,
    letterSpacing: '0.5px',
    display: 'inline-block',
    marginBottom: '16px',
    width: 'fit-content',
  },
  session: {
    fontFamily: 'DM Sans',
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '130%',
    letterSpacing: '0.15px',
    color: '#1F1F1F',
    textTransform: 'uppercase',
    marginTop: '16px',
    marginBottom: '16px',
  },
  date: {
    fontFamily: 'DM Sans',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '130%',
    color: '#251F1F',
    marginBottom: '4px',
  },
  time: {
    fontFamily: 'DM Sans',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '130%',
    color: '#251F1F',
    marginBottom: '16px',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#251F1F',
  },
  timeframeBadge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 400,
    backgroundColor: '#F3F4F6',
    color: '#6B7280',
  },
  description: {
    fontFamily: 'DM Sans',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '150%',
    letterSpacing: '0.15px',
    color: '#251F1F',
    marginBottom: '16px',
  },
  actionsContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  rightButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0,
    height: '100%',
  },
  outlinedButton: {
    boxShadow: 'none !important',
    borderColor: `${colors?.primary || '#148199'}`,
    '&:hover': {
      boxShadow: 'none !important',
      borderColor: `${colors?.primary || '#148199'}`,
    },
  },
}))

function getStatusLabel(
  status: 'upcoming' | 'notAttended' | 'completed',
  t: (key: string) => string
): string | null {
  if (status === 'completed')
    return t('consultations_program_session_completed')
  if (status === 'upcoming') return t('consultations_program_session_upcoming')
  if (status === 'notAttended')
    return t('consultations_program_session_not_attended')
  return null
}

function getStatusColors(status: 'upcoming' | 'notAttended' | 'completed'): {
  backgroundColor: string
  textColor: string
} {
  if (status === 'completed') {
    return {
      backgroundColor: '#D1FAE5',
      textColor: '#065F46',
    }
  }
  if (status === 'upcoming') {
    return {
      backgroundColor: '#E7EDF9',
      textColor: '#07378F',
    }
  }
  if (status === 'notAttended') {
    return {
      backgroundColor: '#FFF3EC',
      textColor: '#E97E19',
    }
  }
  return {
    backgroundColor: '#E7EDF9',
    textColor: '#666666',
  }
}

export const hasRichTextContent = (html?: string): boolean => {
  if (!html) return false

  const text = html
    .replace(/<[^>]*>/g, '') // remove HTML tags
    .replace(/&nbsp;/g, ' ') // remove non-breaking spaces
    .trim()

  return text.length > 0
}

const AppointmentCard: React.FC<AppointmentCardProps> = ({
  status,
  session,
  title,
  subtitle,
  date,
  time,
  description,
  colors,
  programSlug,
  sessionNumber,
  appointment,
  canJoin,
  meetingLink,
  hideStatus = false,
  consultationTypeLabel,
  consultationType,
  consultationSlug,
  onCancelRequested,
}) => {
  const {classes} = useStyles({colors})
  const {t} = useTranslation()
  const {isFertility} = useProductLine()
  const history = useHistory()

  const statusLabel = hideStatus ? null : getStatusLabel(status, t)
  const statusColors = getStatusColors(status)

  const consultationTypeChipSx =
    consultationType === 'emotional_program'
      ? {
          backgroundColor: `${colors?.secondary || '#E1F4F7'} !important`,
          color: `${colors?.primary || '#148199'} !important`,
        }
      : consultationType === 'general'
        ? {
            backgroundColor: '#D1FAE5 !important',
            color: '#065F46 !important',
          }
        : undefined

  const handleRescheduleClick = () => {
    if (!appointment?.canReschedule || !appointment?.calcomBookingUid) {
      return
    }

    if (consultationSlug) {
      history.push(`/consultations/${consultationSlug}/onboarding/reschedule`)
      return
    }

    if (programSlug && sessionNumber) {
      history.push(
        `/consultations/program/${programSlug}/book?session=${sessionNumber}&rescheduleUid=${appointment.calcomBookingUid}`
      )
    }
  }

  const handleJoinMeetingClick = () => {
    if (meetingLink) {
      window.open(meetingLink, '_blank')
    }
  }

  const cancellationUrl = appointment?.calcomBookingUid
    ? getCancellationUrl(appointment.calcomBookingUid)
    : null

  const handleCancelClick = () => {
    if (!cancellationUrl) {
      return
    }

    if (onCancelRequested) {
      onCancelRequested(cancellationUrl)
    }
  }

  return (
    <Box
      className={classes.card}
      sx={{
        alignItems: isFertility ? 'center !important' : 'flex-start',
      }}
    >
      {/* Main Content */}
      <Box className={classes.mainContent}>
        {/* Icon Column */}
        <Box className={classes.iconColumn}>
          <Box className={classes.iconWrap}>
            <CalendarTodayIcon sx={{fontSize: '20px', color: '#6B7280'}} />
          </Box>
        </Box>

        <Box className={classes.contentColumn}>
          {statusLabel && (
            <Typography
              className={classes.statusLabel}
              sx={{
                backgroundColor: statusColors.backgroundColor,
                color: statusColors.textColor,
              }}
            >
              {statusLabel}
            </Typography>
          )}
          {consultationTypeLabel && (
            <Chip
              label={consultationTypeLabel}
              size='medium'
              sx={{
                marginBottom: 1,
                width: 'fit-content',
                ...consultationTypeChipSx,
              }}
            />
          )}
          {!session && (
            <Typography className={classes.session}>
              {t('consultations_appointment_card_personal')}
            </Typography>
          )}
          {session && (
            <Typography className={classes.session}>{session}</Typography>
          )}

          {date && <Typography className={classes.date}>{date}</Typography>}

          {time && <Typography className={classes.time}>{time}</Typography>}

          <Box className={classes.titleContainer}>
            {title && (
              <Typography className={classes.title}>{title}</Typography>
            )}
            {subtitle && (
              <Typography className={classes.timeframeBadge}>
                {subtitle}
              </Typography>
            )}
          </Box>

          {description && (
            <Typography className={classes.description}>
              {description}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Right Button Container */}
      {status === 'upcoming' && (
        <Box className={classes.rightButtonContainer} sx={{gap: '12px'}}>
          <PGButton
            variant='text'
            label={t('common_cancel')}
            onClick={handleCancelClick}
            disabled={!cancellationUrl}
            size='medium'
            className={classes.outlinedButton}
          />

          <PGButton
            variant={isFertility ? 'text' : 'outlined'}
            label={t('consultations_program_fertility_session_reschedule')}
            onClick={handleRescheduleClick}
            disabled={
              !appointment?.canReschedule || !appointment?.calcomBookingUid
            }
            size='medium'
            className={classes.outlinedButton}
          />

          <PGButton
            variant='outlined'
            label={t('consultations_program_fertility_session_join_meeting')}
            className={classes.outlinedButton}
            onClick={handleJoinMeetingClick}
            disabled={!canJoin || !meetingLink}
            size='medium'
          />
        </Box>
      )}
    </Box>
  )
}

export default AppointmentCard
